import { Link } from 'react-router-dom';
import { BarChart3, Building2, LayoutTemplate, MessageSquareText, Search, Sparkles, Star } from 'lucide-react';
import { Reveal, TiltCard } from '../components/motion';
import { FacebookGlyph, InstagramGlyph, LinkedInGlyph, TikTokGlyph } from '../components/BrandGlyphs';
import './serviceGrid.css';

const PRODUCTS = [
  { icon: Building2, name: 'Profile', copy: 'Business profile tracking and guided setup' },
  { icon: Search, name: 'Search', copy: 'Grid and keyword rank tracking' },
  { icon: Star, name: 'Reviews', copy: 'Requests, QR codes, and AI responses' },
  { icon: MessageSquareText, name: 'Social', copy: 'Content calendar and AI planner' },
  { icon: LayoutTemplate, name: 'Website', copy: 'AI website builder and landing pages' },
  { icon: BarChart3, name: 'Reports', copy: 'Campaign, source, and revenue reporting' },
  { icon: Sparkles, name: 'AI Visibility', copy: '15 assistants trained on your details' },
];

/* Mock panels are illustrative of the interface, not customer data. */
export default function ServiceGrid() {
  return (
    <section className="service-grid" id="services">
      <Reveal className="service-grid-head">
        <div className="section-tag">ONE TEAM. EVERY PRODUCT.</div>
        <h2 className="section-title">One team. Every product. Zero vendor juggling.</h2>
        <p className="section-sub">Seven products built to work together — profile, search, reviews, social, website, reports, and AI visibility. One workspace, one monthly invoice.</p>
      </Reveal>

      <div className="product-strip">
        {PRODUCTS.map((product, index) => {
          const Icon = product.icon;
          return (
            <TiltCard as="article" className="product-chip" intensity={7} lift={7} key={product.name} style={{ '--i': index }}>
              <span className="product-chip-icon"><Icon size={17} /></span>
              <strong>{product.name}</strong>
              <small>{product.copy}</small>
            </TiltCard>
          );
        })}
      </div>

      <div className="service-grid-primary">
        <TiltCard as="article" className="service-panel" intensity={3} lift={4}>
          <div className="service-panel-copy">
            <span className="service-eyebrow">PROFILE MANAGEMENT &amp; OPTIMIZATION</span>
            <h3>Show up where local customers are already searching.</h3>
            <p>Track Google Business Profile performance, keep hours and services accurate, and work through guided setup with the team.</p>
            <Link to="/products" className="service-link">Learn more →</Link>
          </div>
          <div className="service-mock">
            <header className="mock-profile-head">
              <span className="mock-avatar" aria-hidden="true">P</span>
              <span className="mock-identity"><strong>Premier HVAC Solutions</strong><small>HVAC Contractor · Coral Springs, FL</small></span>
              <span className="mock-pill is-good">Optimized</span>
            </header>
            <div className="mock-stats">
              <div><b>847</b><small>VIEWS</small></div>
              <div><b>94</b><small>REVIEWS</small></div>
              <div><b>4.9★</b><small>RATING</small></div>
              <div><b>247</b><small>CALLS</small></div>
            </div>
            <div className="mock-chips">
              <span><i className="is-teal" />Weekly posts</span>
              <span><i className="is-teal" />Q&amp;A monitored</span>
              <span><i className="is-amber" />Photos updated</span>
              <span><i className="is-teal" />Services listed</span>
              <span><i className="is-teal" />Hours verified</span>
            </div>
            <p className="mock-note">Example profile</p>
          </div>
        </TiltCard>

        <TiltCard as="article" className="service-panel" intensity={3} lift={4}>
          <div className="service-panel-copy">
            <span className="service-eyebrow">REVIEW MANAGEMENT</span>
            <h3>Build the reputation that wins customers before they call.</h3>
            <p>Send review requests automatically after service, then draft professional responses that never invent facts.</p>
            <Link to="/products" className="service-link">Learn more →</Link>
          </div>
          <div className="service-mock mock-reviews">
            {[
              { stars: 5, quote: 'Best HVAC in Coral Springs. Fast, professional, fair pricing.', time: '4 min ago' },
              { stars: 5, quote: 'Came out same day. Will definitely use again.', time: '1 hr ago' },
              { stars: 5, quote: 'Fixed our AC in under an hour.', time: '3 hr ago', faded: true },
            ].map(review => (
              <div className={`mock-review${review.faded ? ' is-faded' : ''}`} key={review.time}>
                <span className="mock-check" aria-hidden="true">✓</span>
                <div>
                  <strong>Verified Client</strong>
                  <span className="mock-stars" aria-hidden="true">{'★'.repeat(review.stars)}</span>
                  <p>“{review.quote}”</p>
                  <small>✓ Response sent · {review.time}</small>
                </div>
              </div>
            ))}
            <p className="mock-note">Example responses</p>
          </div>
        </TiltCard>
      </div>

      <div className="service-grid-secondary">
        <TiltCard as="article" className="service-tile" intensity={5} lift={5}>
          <span className="service-eyebrow">MONTHLY REPORTING</span>
          <h3>Clear reports. No jargon.</h3>
          <Link to="/results" className="service-link">Learn more →</Link>
          <div className="mock-bars">
            {[
              { label: 'Profile views', value: 62 },
              { label: 'Direction clicks', value: 41 },
              { label: 'Call clicks', value: 88 },
              { label: 'Website visits', value: 35 },
            ].map((row, index) => (
              <div key={row.label} style={{ '--i': index }}>
                <small>{row.label}</small>
                <span className="mock-bar-track"><i style={{ '--value': `${row.value}%` }} /></span>
                <b>+{row.value}%</b>
              </div>
            ))}
          </div>
          <p className="mock-note">Example report</p>
        </TiltCard>

        <TiltCard as="article" className="service-tile" intensity={5} lift={5}>
          <span className="service-eyebrow">LOCAL RANK TRACKING</span>
          <h3>Know exactly where you rank.</h3>
          <Link to="/products" className="service-link">Learn more →</Link>
          <div className="mock-ranks">
            {[
              { term: 'emergency ac repair', rank: 2 },
              { term: 'hvac near me', rank: 4 },
              { term: 'ac installation', rank: 9 },
              { term: 'duct cleaning', rank: 14 },
            ].map(row => (
              <span key={row.term} className={row.rank <= 3 ? 'is-top' : row.rank <= 10 ? 'is-mid' : 'is-low'}>
                {row.term}<b>#{row.rank}</b>
              </span>
            ))}
          </div>
          <p className="mock-note">Example keywords</p>
        </TiltCard>

        <TiltCard as="article" className="service-tile" intensity={5} lift={5}>
          <span className="service-eyebrow">WEBSITE BUILDER</span>
          <h3>A site built to convert local traffic.</h3>
          <Link to="/products" className="service-link">Learn more →</Link>
          <div className="mock-browser">
            <header><i /><i /><i /></header>
            <div className="mock-browser-body">
              <strong>Premier HVAC Solutions</strong>
              <small>Coral Springs, FL · Licensed &amp; insured</small>
              <span className="mock-cta">Book Now</span>
            </div>
            <footer><span>Fast Load</span><span>Mobile</span><span>SEO</span></footer>
          </div>
          <p className="mock-note">Example draft</p>
        </TiltCard>

        <TiltCard as="article" className="service-tile" intensity={5} lift={5}>
          <span className="service-eyebrow">SOCIAL CONTENT</span>
          <h3>Posts that sound like you wrote them.</h3>
          <Link to="/products" className="service-link">Learn more →</Link>
          <div className="mock-social">
            {[
              { name: 'Facebook', detail: '4 posts scheduled', glyph: FacebookGlyph },
              { name: 'Instagram', detail: 'Reel + 3 posts queued', glyph: InstagramGlyph },
              { name: 'TikTok', detail: '2 videos this week', glyph: TikTokGlyph },
              { name: 'LinkedIn', detail: '1 post drafted', glyph: LinkedInGlyph },
            ].map(row => {
              const Glyph = row.glyph;
              return (
                <div key={row.name}>
                  <span className="mock-social-mark"><Glyph size={18} /></span>
                  <span><strong>{row.name}</strong><small>{row.detail}</small></span>
                  <b aria-hidden="true">✓</b>
                </div>
              );
            })}
          </div>
          <p className="mock-note">Example calendar</p>
        </TiltCard>
      </div>
    </section>
  );
}
